import type { ClientOfferSurfaceSummary } from './clientOffer.types';
import type { EngineInput, EngineResult } from './estimate.types';
import {
  calculateQuickApartmentSurface,
  calculateQuickReadySurface,
  calculateQuickRoomSurface,
  calculateQuickSingleWallSurface,
  type ApartmentLayoutMode,
  type SurfaceCalculationResult,
} from './surface/openingsCalculator';

/**
 * The one place the quick estimate turns contractor input into geometry.
 *
 * Everything here is production code used by `QuickPhotoEstimate.tsx`. Tests must
 * import these functions rather than re-implementing them — a test that rebuilds
 * the mapping locally proves only that the test agrees with itself.
 */

const FALLBACK_HEIGHT_M = 2.6;
const FALLBACK_FLOOR_AREA_M2 = 20;
const APARTMENT_FALLBACK_HEIGHT_M = 2.65;
const POLISH_SURFACE_VALUE_FORMATTER = new Intl.NumberFormat('pl-PL', {
  maximumFractionDigits: 2,
});

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/** Formats a measurement for the Polish UI without changing the numeric value. */
export function formatSurfaceValue(value: number): string {
  const safeValue = Number.isFinite(value) ? value : 0;
  return POLISH_SURFACE_VALUE_FORMATTER.format(safeValue);
}

/** Adds the surface unit only after the numeric value has been formatted for the UI. */
export function formatSurfaceM2(value: number): string {
  return `${formatSurfaceValue(value)} m²`;
}

export function parsePositiveNumber(value: string): number | undefined {
  const parsed = Number(value.replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

export function parseNonNegativeNumber(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value.replace(',', '.'));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

export function parseNonNegativeInteger(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value.replace(',', '.'));
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
}

/** The subset of the quick-estimate form that describes the surface. */
export interface QuickSurfaceFormFields {
  surfaceMode: 'ready' | 'room' | 'apartment' | 'wall';
  readyWorkingArea: string;
  roomLength: string;
  roomWidth: string;
  manualHeight: string;
  doorCount: string;
  largeOpeningsArea: string;
  includeCeiling: boolean;
  apartmentArea: string;
  apartmentHeight: string;
  apartmentDoorCount: string;
  apartmentLargeOpeningsArea: string;
  apartmentLayout: ApartmentLayoutMode;
  bathroomArea: string;
  bathroomCeilingOnly: boolean;
}

/** Form fields → the surface calculation the whole pipeline is built on. */
export function calculateSurfaceForQuickForm(
  form: QuickSurfaceFormFields
): SurfaceCalculationResult {
  if (form.surfaceMode === 'ready') {
    return calculateQuickReadySurface({
      workingAreaM2: parsePositiveNumber(form.readyWorkingArea),
    });
  }

  if (form.surfaceMode === 'apartment') {
    return calculateQuickApartmentSurface({
      apartmentAreaM2: parsePositiveNumber(form.apartmentArea),
      heightM: parsePositiveNumber(form.apartmentHeight) ?? APARTMENT_FALLBACK_HEIGHT_M,
      doorCount: parseNonNegativeInteger(form.apartmentDoorCount),
      largeOpeningsAreaM2: parseNonNegativeNumber(form.apartmentLargeOpeningsArea),
      bathroomAreaM2: parseNonNegativeNumber(form.bathroomArea),
      bathroomCeilingOnly: form.bathroomCeilingOnly,
      layoutMode: form.apartmentLayout,
      includeCeiling: form.includeCeiling,
    });
  }

  if (form.surfaceMode === 'wall') {
    return calculateQuickSingleWallSurface({
      lengthM: parsePositiveNumber(form.roomLength),
      heightM: parsePositiveNumber(form.manualHeight),
      doorCount: parseNonNegativeInteger(form.doorCount),
      largeOpeningsAreaM2: parseNonNegativeNumber(form.largeOpeningsArea),
    });
  }

  return calculateQuickRoomSurface({
    lengthM: parsePositiveNumber(form.roomLength),
    widthM: parsePositiveNumber(form.roomWidth),
    heightM: parsePositiveNumber(form.manualHeight),
    doorCount: parseNonNegativeInteger(form.doorCount),
    largeOpeningsAreaM2: parseNonNegativeNumber(form.largeOpeningsArea),
    includeCeiling: form.includeCeiling,
  });
}

export interface QuickAreaInputs {
  /** Floor area the screen carries as the contractor's manual entry. */
  manualAreaM2?: number;
  /** Working area used for the "did we get any measurement" guards. */
  workingAreaM2?: number;
  /** What the engine receives — room mode re-derives it from length × width. */
  engineManualWorkingAreaM2?: number;
  heightM: number;
}

/** The area inputs the screen derives from a surface calculation before pricing. */
export function resolveQuickAreaInputs(surface: SurfaceCalculationResult): QuickAreaInputs {
  const manualAreaM2 = surface.floorAreaM2 || undefined;
  const workingAreaM2 = surface.finalWorkingAreaM2 || undefined;

  return {
    manualAreaM2,
    workingAreaM2,
    engineManualWorkingAreaM2: surface.mode === 'room' ? undefined : workingAreaM2,
    heightM: surface.heightM,
  };
}

export interface QuickEngineGeometryInput {
  surfaceCalculation?: SurfaceCalculationResult;
  manualAreaM2?: number;
  manualWorkingAreaM2?: number;
  manualHeightM?: number;
  /** Photo context only — never a measurement, only a fallback for missing input. */
  visionDoorCount?: number;
  visionBigWindowsArea?: number;
}

export type QuickEngineGeometry = Pick<
  EngineInput,
  | 'projectMode'
  | 'length'
  | 'width'
  | 'height'
  | 'floorArea'
  | 'manualWorkingAreaM2'
  | 'doorCount'
  | 'bigWindowsArea'
  | 'includeCeiling'
> & {
  height: number;
  floorArea: number;
  doorCount: number;
  bigWindowsArea: number;
  /** Floor area as shown to the contractor before the engine rounds anything. */
  displayAreaM2: number;
};

/** Surface calculation → the geometry fields handed to the estimate engine. */
export function buildQuickEngineGeometry(
  input: QuickEngineGeometryInput
): QuickEngineGeometry {
  const surfaceCalculation = input.surfaceCalculation;
  const isDirectWorkingAreaMode =
    surfaceCalculation?.mode === 'ready' || surfaceCalculation?.mode === 'wall';
  const height = surfaceCalculation?.heightM ?? input.manualHeightM ?? FALLBACK_HEIGHT_M;
  const displayAreaM2 = isDirectWorkingAreaMode
    ? surfaceCalculation.finalWorkingAreaM2
    : surfaceCalculation?.floorAreaM2 ??
      input.manualAreaM2 ??
      (input.manualWorkingAreaM2
        ? input.manualWorkingAreaM2 / FALLBACK_HEIGHT_M
        : FALLBACK_FLOOR_AREA_M2);
  const floorArea = isDirectWorkingAreaMode
    ? surfaceCalculation.finalWorkingAreaM2
    : surfaceCalculation?.floorAreaM2 ??
      (input.manualWorkingAreaM2
        ? input.manualWorkingAreaM2 / FALLBACK_HEIGHT_M
        : displayAreaM2);
  const length =
    surfaceCalculation?.mode !== 'room'
      ? undefined
      : surfaceCalculation.lengthM ??
        (input.manualWorkingAreaM2 ? undefined : Math.sqrt(floorArea));
  const width =
    surfaceCalculation?.mode !== 'room'
      ? undefined
      : surfaceCalculation.widthM ??
        (input.manualWorkingAreaM2 ? undefined : floorArea / (length ?? 1));

  return {
    projectMode:
      surfaceCalculation?.mode === 'apartment'
        ? 'apartment'
        : surfaceCalculation
          ? 'room'
          : 'apartment',
    length,
    width,
    height,
    floorArea,
    manualWorkingAreaM2: input.manualWorkingAreaM2,
    doorCount: surfaceCalculation?.doorCount ?? input.visionDoorCount ?? 1,
    bigWindowsArea:
      surfaceCalculation?.largeOpeningsDeductionM2 ?? input.visionBigWindowsArea ?? 0,
    includeCeiling: surfaceCalculation?.includeCeiling,
    displayAreaM2,
  };
}

/**
 * The working area exactly as the screen renders it. The saved project and the
 * PDF must start from this same rounded number, not from a second rounding.
 */
export function resolveQuickWorkingAreaM2(
  estimateMeta: Pick<EngineResult['meta'], 'totalWorkingArea'>
): number {
  return round1(estimateMeta.totalWorkingArea);
}

export interface QuickEstimatedAreaSource {
  /** Rounded working area as rendered on screen, when the summary carries one. */
  workingAreaM2?: number;
  estimateMeta: Pick<EngineResult['meta'], 'totalWorkingArea'>;
}

/**
 * The single working-area number shared by the screen, the saved project and the
 * client PDF. If these ever disagree, the contractor is quoting one area and
 * invoicing another.
 */
export function resolveQuickEstimatedAreaM2(source: QuickEstimatedAreaSource): number {
  return source.workingAreaM2 ?? source.estimateMeta.totalWorkingArea;
}

/** Geometry block handed to the client PDF for non-paving estimates. */
export function buildQuickOfferSurfaceSummary(input: {
  surfaceCalculation?: SurfaceCalculationResult;
  estimateMeta: EngineResult['meta'];
}): ClientOfferSurfaceSummary {
  const surface = input.surfaceCalculation;
  const meta = input.estimateMeta;

  return {
    surfaceMode: surface?.mode,
    apartmentAreaM2: surface?.apartmentAreaM2,
    bathroomAreaM2: surface?.bathroomAreaM2,
    bathroomModeLabel: surface?.bathroomModeLabel,
    confirmationStatus: surface?.confirmationStatus,
    floorAreaM2: meta.floorArea,
    perimeterM: meta.perimeter,
    wallGrossAreaM2: meta.wallsGross,
    doorDeductionM2: meta.doorsDeduction,
    largeOpeningsDeductionM2: meta.bigWindowsDeduction,
    wallNetAreaM2: meta.wallsNet,
    ceilingAreaM2: meta.ceilingArea,
    finalWorkingAreaM2: meta.totalWorkingArea,
  };
}

/**
 * An explicit zero deduction is a result, not missing data. Hiding "0 m²" leaves
 * the contractor unable to tell whether the openings were counted as nothing or
 * never considered at all.
 */
export function formatQuickDeductionM2(value: number): string {
  const safeValue = Number.isFinite(value) ? value : 0;
  return safeValue > 0 ? `-${formatSurfaceM2(safeValue)}` : formatSurfaceM2(0);
}

export type QuickGeometrySourceTone = 'manual' | 'local';

export type QuickGeometryField =
  | 'apartmentArea'
  | 'readyWorkingArea'
  | 'height'
  | 'bathroomMode'
  | 'floorArea'
  | 'perimeter'
  | 'wallGross'
  | 'doorDeduction'
  | 'largeOpeningsDeduction'
  | 'wallNet'
  | 'ceilingArea'
  | 'finalWorkingArea';

/** Fields the contractor types in directly. */
const ENTERED_FIELDS: readonly QuickGeometryField[] = [
  'apartmentArea',
  'readyWorkingArea',
  'height',
  'bathroomMode',
];

/**
 * Source badge semantics: `manual` means the contractor typed this value,
 * `local` means the app derived it from those inputs. Deriving 46.6 m² of net
 * walls from 4 × 5 × 2.7 is the app's arithmetic — labelling it "manual" would
 * credit the contractor with a number they never entered.
 */
export function getQuickGeometrySource(
  field: QuickGeometryField,
  mode?: SurfaceCalculationResult['mode']
): QuickGeometrySourceTone {
  if (ENTERED_FIELDS.includes(field)) return 'manual';
  // In apartment mode the floor area IS the area the contractor typed.
  if (field === 'floorArea' && mode === 'apartment') return 'manual';
  return 'local';
}
